import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from '../../../../components/UI/Button';
import { Heading, Text } from '../../../../components/Typography';
import { AppDispatch, RootState } from '../../../../store';
import { setSelectedDocumentIdsAsync } from '../../../../store/summaryTest/slice';
import DocumentItem from './DocumentItem';
import styles from './DocumentList.module.scss';

const PAGE_SIZE = 10;

const DocumentList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId, documents, selectedDocuments, isLoadingDocuments, isLoadingSections } =
    useSelector((state: RootState) => state.summaryTest);
  const selectedDocumentIds = Object.keys(selectedDocuments);
  const loading = isLoadingDocuments || !companyId;

  const currentSelectedSectionIdxMap = useMemo(() => {
    const map: { [documentId: string]: number[] } = {};
    for (const docId of selectedDocumentIds) {
      map[docId] = selectedDocuments[docId]?.selectedSectionIdx || [];
    }
    return map;
  }, [selectedDocumentIds, selectedDocuments]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pendingDocumentIds, setPendingDocumentIds] = useState<Set<string>>(new Set());

  // Calculate which documents to show based on current page
  const displayedDocuments = documents.slice(0, currentPage * PAGE_SIZE);
  const hasMore = displayedDocuments.length < documents.length;

  const handleDocumentToggle = async (documentId: string) => {
    // Immediately update the pending state for optimistic UI
    const newPendingIds = new Set(pendingDocumentIds);
    newPendingIds.add(documentId);
    setPendingDocumentIds(newPendingIds);

    const newDocumentIds = selectedDocumentIds.includes(documentId)
      ? selectedDocumentIds.filter((id) => id !== documentId)
      : [...selectedDocumentIds, documentId];

    try {
      await dispatch(
        setSelectedDocumentIdsAsync({
          documentIds: newDocumentIds,
          currentDocuments: documents,
          currentSelectedSectionIdxMap,
        }),
      ).unwrap();
    } catch (error) {
      console.error('Error updating document selection:', error);
      // Optionally show an error message to the user
    } finally {
      // Remove from pending state when done
      const updatedPendingIds = new Set(pendingDocumentIds);
      updatedPendingIds.delete(documentId);
      setPendingDocumentIds(updatedPendingIds);
    }
  };

  const handleDeselectAllDocuments = async () => {
    // Add all currently selected document IDs to pending state
    setPendingDocumentIds(new Set(selectedDocumentIds));

    try {
      await dispatch(
        setSelectedDocumentIdsAsync({
          documentIds: [],
          currentDocuments: documents,
          currentSelectedSectionIdxMap,
        }),
      ).unwrap();
    } catch (error) {
      console.error('Error deselecting all documents:', error);
    } finally {
      setPendingDocumentIds(new Set());
    }
  };

  const handleShowMore = () => {
    setCurrentPage(currentPage + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level={3}>Documents</Heading>
        <div className={styles.controls}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleDeselectAllDocuments}
            disabled={isLoadingDocuments || selectedDocumentIds.length === 0 || isLoadingSections}
            loading={isLoadingSections && selectedDocumentIds.length > 0}
          >
            Deselect All
          </Button>
        </div>
      </div>

      {loading && (
        <Text color="neutral-500" className={styles.loadingContainer}>
          Loading documents...
        </Text>
      )}

      {!loading && documents.length === 0 && (
        <Text color="neutral-500" className={styles.emptyState}>
          No documents found.
        </Text>
      )}

      {!loading && displayedDocuments.length > 0 && (
        <>
          <div className={styles.documentList}>
            {displayedDocuments.map((document) => {
              const isPending = pendingDocumentIds.has(document.id);
              const isSelected = selectedDocumentIds.includes(document.id);

              return (
                <DocumentItem
                  key={document.id}
                  document={document}
                  isSelected={isSelected}
                  isPending={isPending}
                  onToggle={handleDocumentToggle}
                />
              );
            })}
          </div>

          {hasMore && (
            <div className={styles.showMoreContainer}>
              <Button
                variant="secondary"
                size="small"
                onClick={handleShowMore}
                disabled={isLoadingDocuments}
              >
                Show 10 More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentList;
